/**
 * Single source of truth for Clari Chat AI catalog categories (mirrors the model picker in Clari Chat).
 * Used by team drag-drop, multichat, double-chat model dialogs, and related UIs.
 */

export interface ClariChatCatalogModel {
  name: string;
  value: string;
}

export interface ClariChatModelCategory {
  title: string;
  models: ClariChatCatalogModel[];
}

export const CLARI_CHAT_MODEL_CATEGORIES: ClariChatModelCategory[] = [
  {
    title: '👨‍💻 Elite Code Generation',
    models: [
      { name: 'Opus 4.7', value: 'opus47' },
      { name: 'Kimi 2.5', value: 'kimik25' },
      { name: 'Qwen 3 Coder', value: 'qwen' },
      { name: 'ChatGPT 5.2', value: 'chatgpt5' },
      { name: 'Codestral', value: 'codestralto' },
      { name: 'Claude Opus 4.5', value: 'opus45' },
      { name: 'Sonnet 4.5', value: 'sonnet45' }
    ]
  },
  {
    title: '🧠 Deep Reasoning Models',
    models: [
      { name: 'Writer X5', value: 'palmx' },
      { name: 'Gemini Pro', value: 'gemini' },
      { name: 'Qwen 3 Coder', value: 'qwen' },
      { name: 'GLM 5', value: 'glm5' },
      { name: 'Claude Opus 4.5', value: 'opus45' }
    ]
  },
  {
    title: '🎯 Vision Capable Models',
    models: [
      { name: 'Haiku 4.5', value: 'haiku45' },
      { name: 'Pixtral', value: 'pixtralimage' },
      { name: 'Pegasus 1.2', value: 'pegasus' },
    ]
  },
  {
    title: '💡 Large Context Windows',
    models: [
      { name: 'DeepSeek V3.2', value: 'deepv3' },
      { name: 'Llama 4', value: 'maverick' }
    ]
  },
  {
    title: '💡 Enterprise Models',
    models: [
      { name: '⌘ Instruct', value: 'commandLight' },
      { name: 'DeepSeek R1', value: 'deepseek' },
      { name: '⌘ R+', value: 'cohere' },
      { name: 'Writer X5', value: 'palmx' },
      { name: 'Meta Scout', value: 'scout' },
      { name: 'Jamba Large', value: 'jamba' },
      { name: 'Codestral', value: 'codestralto' }
    ]
  },
  {
    title: '🧾 Instruct Models (AWS Bedrock)',
    models: [
      { name: 'Command R', value: 'commandRInstruct' },
      { name: 'Llama 3.1', value: 'llama31Instruct' },
      { name: 'Nova Lite', value: 'novalite' },
      { name: 'Nova Pro', value: 'novapro' },
      { name: '⌘ R+', value: 'cohere' },
      { name: 'Llama 4', value: 'maverick' },
      { name: 'Haiku 4.5', value: 'haiku45' },
      { name: 'Sonnet 4.5', value: 'sonnet45' },
      { name: 'GLM 5', value: 'glm5' }
    ]
  }
];

/** Normalized ids that require a signed-in (non-anonymous) user — Claude Opus 4.7, ChatGPT 5.4 family, Gemini Pro, Codestral. */
const CLARI_SIGNED_IN_ONLY_NORMALIZED = new Set(['opus46', 'opus47', 'chatgpt', 'gemini', 'codestralto']);

/** Aligns with AI catalog `normalizeChatGptValue` for ChatGPT variants. */
export function normalizeClariCatalogModelValue(value: string | null | undefined): string {
  const normalized = (value || '').trim().toLowerCase();
  if (!normalized) {
    return '';
  }
  if (normalized === 'opus6') {
    return 'opus46';
  }
  if (normalized === 'opus7') {
    return 'opus47';
  }
  if (['kimi25', 'kimi-2.5', 'kimi2.5', 'moonshot-kimi'].includes(normalized)) {
    return 'kimik25';
  }
  if (normalized === 'chatgpt') {
    return 'chatgpt54';
  }
  if (['chatgpt52', 'chatgpt5'].includes(normalized)) {
    return 'chatgpt5';
  }
  if (normalized === 'chatgpt54') {
    return 'chatgpt54';
  }
  if (normalized === 'chatgpt4') {
    return 'chatgpt54';
  }
  if (['haiku', 'haiku35', 'haiku3', 'haiku3.5', 'haiku4', 'haiku4.5'].includes(normalized)) {
    return 'haiku45';
  }
  if (['pegusus', 'pegasus1.2', 'pegasus-1.2'].includes(normalized)) {
    return 'pegasus';
  }
  return normalized;
}

export function isClariSignedInOnlyCatalogModel(value: string | null | undefined): boolean {
  return CLARI_SIGNED_IN_ONLY_NORMALIZED.has(normalizeClariCatalogModelValue(value));
}

/** Logo key under assets/logos/ (matches multichat / Clari patterns). */
export const CLARI_CHAT_MODEL_VENDOR_BY_ID: Record<string, string> = {
  opus46: 'anthropic',
  opus47: 'anthropic',
  kimik25: 'moonshot',
  opus37: 'anthropic',
  opus6: 'anthropic', // legacy id → normalized to opus46
  opus45: 'anthropic',
  haiku45: 'anthropic',
  haiku: 'anthropic',
  sonnet45: 'anthropic',
  chatgpt5: 'openai',
  chatgpt54: 'openai',
  minichatgpt54: 'openai',
  codestralto: 'mistral',
  mistrallarge: 'mistral',
  pixtralimage: 'mistral',
  pegasus: 'twelvelabs',
  palmx: 'writer',
  gemini: 'google',
  deepv3: 'deepseek',
  deepseek: 'deepseek',
  maverick: 'meta',
  qwen: 'qwen',
  glm5: 'zai',
  scout: 'meta',
  cohere: 'cohere',
  commandLight: 'cohere',
  jamba: 'ai21',
  novapro: 'amazon',
  commandRInstruct: 'cohere',
  llama31Instruct: 'meta',
  novalite: 'amazon'
};

export function getClariChatModelsFlat(): { id: string; name: string }[] {
  const seen = new Set<string>();
  const out: { id: string; name: string }[] = [];
  for (const cat of CLARI_CHAT_MODEL_CATEGORIES) {
    for (const m of cat.models) {
      if (seen.has(m.value)) continue;
      seen.add(m.value);
      out.push({ id: m.value, name: m.name });
    }
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

export function getClariChatModelNameMap(): Record<string, string> {
  return Object.fromEntries(getClariChatModelsFlat().map((m) => [m.id, m.name]));
}

/** Bedrock instruct aliases excluded from team / DoubleChat pickers (matches multichat). */
export const CLARI_CHAT_TEAM_COMPARE_BLOCKED_IDS = ['llama31Instruct', 'commandRInstruct'] as const;

/** Flat catalog models for drag-drop pickers (same entries as AI catalog, minus blocked ids). */
export function getClariChatModelsForTeamComparePickers(): { id: string; name: string }[] {
  const blocked = new Set<string>(CLARI_CHAT_TEAM_COMPARE_BLOCKED_IDS);
  return getClariChatModelsFlat().filter((m) => !blocked.has(m.id) && m.id !== 'opus46' && m.id !== 'opus6');
}

/**
 * Normalize model ids for compare / team pickers (alias keys align with {@link FlameThrowerService}).
 * Does not fall back to a default model when unknown — returns the normalized token.
 */
export function normalizeFlamePickerModelId(value?: string | null): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) {
    return '';
  }
  const fromCatalog = normalizeClariCatalogModelValue(raw);
  const base = fromCatalog || raw;
  const aliases: Record<string, string> = {
    llama4: 'maverick',
    llama: 'maverick',
    palmyra: 'palmx',
    geminiflash3: 'gemini3flash',
    'gemini-flash-3': 'gemini3flash',
    deepseekv3: 'deepv3',
    'deepseek-v3': 'deepv3',
    pixtral: 'pixtralimage',
    haiku: 'haiku45',
    sonnet: 'sonnet45',
    chatgpt: 'chatgpt54',
    chatgpt5: 'chatgpt5',
    chatgpt52: 'chatgpt5',
    chatgpt54: 'chatgpt54',
    qwen3: 'qwen',
    'qwen-3': 'qwen',
    'qwen-coder': 'qwen',
    'glm-5': 'glm5',
    glm: 'glm5',
    kimi25: 'kimik25',
    'kimi-2.5': 'kimik25',
    'kimi2.5': 'kimik25',
    moonshotkimi: 'kimik25'
  };
  return aliases[raw] || aliases[base] || base;
}
